import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function ContinuousAuditEvidencePage() {
  const faqs = [
    {
      question: 'What does continuous audit evidence include?',
      answer: 'EvidentTrail records approvals, reviews, branch protection snapshots, drift changes, and exception records as they occur, linking them into a single audit timeline.'
    },
    {
      question: 'Do you store source code?',
      answer: 'No. Evidence collection is metadata-only. Source code and free-text content are not stored.'
    },
    {
      question: 'What formats are evidence packs available in?',
      answer: 'Evidence packs include PDF, CSV, JSON, and manifest files with SHA-256 checksums for verification.'
    },
    {
      question: 'Why is continuous evidence better than point-in-time collection?',
      answer: 'Continuous evidence avoids gaps by capturing control changes when they happen, rather than relying on retroactive screenshots or exports.'
    }
  ];

  return (
    <MarketingLayout
      title="Continuous Audit Evidence for Change Control | EvidentTrail"
      description="Continuous audit evidence from GitHub approvals, branch protection drift, and exception tracking. Export evidence packs with SHA-256 checksums."
      h1="Continuous Audit Evidence"
      heroText="Capture approvals, branch protection drift, and exception workflows in a continuous audit timeline with exportable evidence packs."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Continuous Audit Evidence', url: 'https://evidenttrail.com/continuous-audit-evidence' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Continuous Evidence Includes</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail records GitHub change control events as they occur. The audit timeline
              links pull requests, approvals, branch protection snapshots, drift changes, and
              exception records in a single chronological view. All collection is read-only and
              metadata-only; source code and free-text content are not stored.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Approvals and Reviews</h3>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li>• Reviewer identity and approval timestamps</li>
                  <li>• Required checks status and approval thresholds</li>
                  <li>• Linked pull request metadata</li>
                </ul>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Drift and Exceptions</h3>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li>• Branch protection snapshots and diffs</li>
                  <li>• Severity classification for configuration changes</li>
                  <li>• Exception lifecycle records for bypasses</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Evidence Packs and Retention</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Evidence packs can be generated on demand via the evidence tools and API. Each pack
              includes PDF, CSV, JSON, and manifest exports plus SHA-256 checksums for tamper-evident
              verification. Retention is plan-based with 7, 30, or 90 day windows, and expired packs
              are purged automatically.
            </p>
            <div className={tokens.card}>
              <h3 className={`${tokens.heading.h3} mb-3`}>Export Formats</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>• PDF evidence report</li>
                <li>• CSV and JSON data exports</li>
                <li>• Manifest file with checksums</li>
                <li>• Evidence pack identifiers and timestamps</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Why Continuous Matters</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Continuous evidence avoids point-in-time gaps. Drift detection captures branch
              protection changes when they happen, and exception tracking preserves context for
              emergency merges. The audit timeline ties approvals, configuration states, and
              exceptions to the same change record for audit review.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Outcomes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/engineering-audit-toil" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Reduce Audit Toil</h3>
              <p className="text-sm text-slate-400">Replace manual evidence work with automated exports.</p>
            </Link>
            <Link to="/auditor-self-serve" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Auditor Self-Serve</h3>
              <p className="text-sm text-slate-400">Enable guest evidence packs without permanent access.</p>
            </Link>
            <Link to="/iso-27001-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">ISO 27001 GitHub Guide</h3>
              <p className="text-sm text-slate-400">Align GitHub change control evidence to ISO 27001 A.8.32 requirements.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Evidence Paths</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/features/audit-timeline" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Audit Timeline</h3>
              <p className="text-sm text-slate-400">See how approvals and exceptions link in a timeline.</p>
            </Link>
            <Link to="/features/drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Drift Detection</h3>
              <p className="text-sm text-slate-400">Track branch protection changes with severity levels.</p>
            </Link>
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">PR Approvals Tool</h3>
              <p className="text-sm text-slate-400">Generate a tamper-evident approvals evidence pack.</p>
            </Link>
            <Link to="/secure-evidence-sharing" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Secure Evidence Sharing</h3>
              <p className="text-sm text-slate-400">Share evidence packs with time-limited auditor access.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
